import React, { useEffect, useMemo, useState } from "react";
import InfoPage from "../InfoPage";
import { getAuthToken } from '../../utils/authHelpers';
import BlurredGate from '../../components/BlurredGate';
import ReviewShareCard from '../../components/ReviewShareCard';
import ReviewShareModal from '../../components/ReviewShareModal';
import './ReviewsFeed.sass';

// Shape the API review into the props the share card + modal expect.
const toModalReview = (review) => ({
  id: review.id,
  reviewType: review.reviewType,
  reviewText: review.reviewText,
  rating: review.rating,
  subjectName: review.subjectName,
  subjectHandle: review.subjectHandle || null,
  reviewerHandle: review.reviewerHandle || null,
  // Defaults: background = subject's pic, circle = author's pic; custom
  // uploads (heroImageUrl / circleImageUrl) override.
  backgroundImageUrl: review.heroImageUrl ?? null,
  circleImageUrl: review.circleImageUrl ?? null,
  subjectAvatarUrl: review.subjectAvatar || null,
  reviewerAvatarUrl: review.reviewerAvatar || null,
});

const ShareIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const ReviewGridCard = ({ review, onOpen, highlighted }) => {
  const subjectLabel = review.subjectHandle ? `@${review.subjectHandle}` : review.subjectName;
  const reviewerLabel = review.reviewerHandle ? `@${review.reviewerHandle}` : (review.reviewerName || '');
  return (
    <div
      id={`review-${review.id}`}
      className={`rf-card${highlighted ? ' rf-card--highlighted' : ''}`}
    >
      <button type="button" className="rf-card-preview" onClick={() => onOpen(review)} aria-label="Open share card">
        <ReviewShareCard
          backgroundImageUrl={review.heroImageUrl ?? null}
          circleImageUrl={review.circleImageUrl ?? null}
          subjectAvatarUrl={review.subjectAvatar || null}
          reviewerAvatarUrl={review.reviewerAvatar || null}
          reviewText={review.reviewText}
          rating={review.rating}
          subjectName={review.subjectName}
          subjectHandle={review.subjectHandle}
          reviewerHandle={review.reviewerHandle}
          reviewType={review.reviewType}
        />
      </button>
      <div className="rf-card-foot">
        <div className="rf-card-handles">
          {subjectLabel}{reviewerLabel ? <span className="rf-card-x"> × </span> : null}{reviewerLabel}
        </div>
        <button type="button" className="rf-card-share" onClick={() => onOpen(review)} title="Share / download">
          <ShareIcon />
        </button>
      </div>
    </div>
  );
};

const ReviewsFeed = ({ type = 'creator' }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  const [shareReview, setShareReview] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [search, setSearch] = useState('');
  // { creatorProfileId, hostProfileId, isAdmin } for the logged-in user, or null
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const email = typeof window !== 'undefined' ? window.localStorage.getItem('postaway_email') || '' : '';
        if (email) {
          const res = await fetch('/api/access/subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const data = await res.json();
          if (res.ok && data.ok) {
            setMembershipTier(data.membershipTier || 'all_access');
          }
        }
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const token = getAuthToken();
        const res = await fetch(`${apiBase}/api/reviews/feed?type=${type}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.ok) {
          setReviews(data.reviews || []);
          setViewer(data.viewer || null);
        } else {
          setError('Could not load reviews');
        }
      } catch (err) {
        console.error('Failed to load reviews:', err);
        setError('Could not load reviews');
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [type]);

  // Search across handles, names, location, and review text.
  const filteredReviews = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reviews;
    return reviews.filter((r) =>
      [r.subjectName, r.subjectHandle, r.reviewerName, r.reviewerHandle, r.propertyName, r.propertyLocation, r.reviewText]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [reviews, search]);

  // Deep link: ?review=<id> scrolls to and briefly highlights that card.
  useEffect(() => {
    if (loading || reviews.length === 0) return undefined;
    const targetId = new URLSearchParams(window.location.search).get('review');
    if (!targetId) return undefined;
    if (!reviews.some(r => String(r.id) === String(targetId))) return undefined;

    let clearTimer;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`review-${targetId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedId(String(targetId));
      clearTimer = setTimeout(() => setHighlightedId(null), 2600);
    }, 150);
    return () => {
      clearTimeout(scrollTimer);
      if (clearTimer) clearTimeout(clearTimer);
    };
  }, [loading, reviews]);

  // Everyone can open the card to download/copy. Only the users mentioned in
  // a review (its subject or its author) — or the super-admin — may edit its
  // photos.
  const canEditReview = (review) => {
    if (!viewer) return false;
    if (viewer.isAdmin) return true;
    return !!(
      (viewer.creatorProfileId && (
        (review.subjectProfileType === 'creator' && review.subjectProfileId === viewer.creatorProfileId) ||
        (review.reviewerProfileType === 'creator' && review.reviewerProfileId === viewer.creatorProfileId)
      )) ||
      (viewer.hostProfileId && (
        (review.subjectProfileType === 'host' && review.subjectProfileId === viewer.hostProfileId) ||
        (review.reviewerProfileType === 'host' && review.reviewerProfileId === viewer.hostProfileId)
      ))
    );
  };

  const handleOpen = (review) => {
    setShareReview({ ...toModalReview(review), _canEdit: canEditReview(review) });
  };

  // Keep the feed in sync after photos are edited from the modal.
  const handleImagesUpdated = (changes) => {
    if (!shareReview) return;
    setReviews(prev => prev.map(r => {
      if (r.id !== shareReview.id) return r;
      const next = { ...r };
      if (Object.prototype.hasOwnProperty.call(changes, 'heroImageUrl')) next.heroImageUrl = changes.heroImageUrl;
      if (Object.prototype.hasOwnProperty.call(changes, 'circleImageUrl')) next.circleImageUrl = changes.circleImageUrl;
      return next;
    }));
    setShareReview(prev => prev ? {
      ...prev,
      ...(Object.prototype.hasOwnProperty.call(changes, 'heroImageUrl') ? { backgroundImageUrl: changes.heroImageUrl } : {}),
      ...(Object.prototype.hasOwnProperty.call(changes, 'circleImageUrl') ? { circleImageUrl: changes.circleImageUrl } : {}),
    } : prev);
  };

  const title = type === 'host' ? 'Host Reviews' : 'Creator Reviews';
  const subtitle = type === 'host'
    ? 'See what creators are saying about the hosts they have collaborated with on Postaway.'
    : 'See what hosts are saying about the creators they have collaborated with on Postaway.';

  const content = (
    <div className="reviews-feed-page">
      <div className="reviews-feed-header">
        <h2 className="reviews-feed-title">{title}</h2>
        <p className="reviews-feed-subtitle">{subtitle}</p>
      </div>

      {!loading && !error && reviews.length > 0 && (
        <div className="reviews-feed-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search by handle, name, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="reviews-feed-search-clear" onClick={() => setSearch('')} aria-label="Clear search">×</button>
          )}
        </div>
      )}

      {loading && (
        <div className="reviews-feed-loading">Loading reviews...</div>
      )}

      {error && (
        <div className="reviews-feed-error">{error}</div>
      )}

      {!loading && !error && reviews.length === 0 && (
        <div className="reviews-feed-empty">
          <p>No reviews yet. Collaborations are just getting started!</p>
        </div>
      )}

      {!loading && !error && reviews.length > 0 && filteredReviews.length === 0 && (
        <div className="reviews-feed-empty">
          <p>No reviews match &ldquo;{search}&rdquo;.</p>
        </div>
      )}

      {!loading && !error && filteredReviews.length > 0 && (
        <div className="reviews-feed-grid">
          {filteredReviews.map((review) => (
            <ReviewGridCard
              key={review.id}
              review={review}
              onOpen={handleOpen}
              highlighted={String(review.id) === highlightedId}
            />
          ))}
        </div>
      )}

      <ReviewShareModal
        isOpen={!!shareReview}
        onClose={() => setShareReview(null)}
        review={shareReview}
        canEdit={!!shareReview?._canEdit}
        onImagesUpdated={handleImagesUpdated}
      />
    </div>
  );

  if (membershipTier === 'basic') {
    const userType = (typeof window !== 'undefined' && window.localStorage.getItem('postaway_user_type')) || 'host';
    return (
      <InfoPage below={
        <BlurredGate userType={userType}>
          {content}
        </BlurredGate>
      } disableStamps customMinHeight={600} showInlineLivePulse />
    );
  }

  return <InfoPage below={content} disableStamps customMinHeight={600} showInlineLivePulse />;
};

export default ReviewsFeed;
